import { useReducer, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { orderBy } from 'lodash';
import GET_VENDOR_PRODUCT_GROUP, {
  IGetVendorProductGroupByIdData,
  IGetVendorProductGroupByIdVars
} from '@graphql/VendorVariant/queries/get-vendor-product-group-by-id';
import IVendorProductGroup, {
  IBlogPostCardProps,
  IAttributeFilterInput,
  IAttributeFilter
} from '@models/VendorVariant/i-vendor-product-group';
import {
  IVendorVariantInternal,
} from '@models/VendorVariant/i-vendor-variant';

interface IProductGroupPageState {
  blogPosts: IBlogPostCardProps[];
  selectedAttributeFilters: IAttributeFilterInput[];
  selectedVendorVariant: IVendorVariantInternal;
  vendorVariants: IVendorVariantInternal[];
  attributeFilters: IAttributeFilter[];
}

enum actions {
  TOGGLE_FILTER = 'TOGGLE_FILTER',
  SET_VENDOR_PRODUCT_GROUP = 'SET_VENDOR_PRODUCT_GROUP',
  SET_SELECTED_VENDOR_VARIANT = 'SET_SELECTED_VENDOR_VARIANT'
}

export default function useProductGroupState(
  initialVendorProductGroup: IVendorProductGroup,
  initialVendorVariantId?: string,
): [
  IProductGroupPageState,
  boolean,
  {
    toggleAttributeFilter: (attributeFilterInput: IAttributeFilterInput) => void;
    selectVendorVariant: (vendorVariantId: number) => void;
  }
] {
  const {
    id,
    vendorVariants: initialVendorVariants,
    attributeFilters: initialAttributeFilters
  } = initialVendorProductGroup;

  // using a reducer to manage state
  const [state, dispatch] = useReducer(productGroupStateReducer, {
    blogPosts: initialVendorProductGroup.blogPosts || [],
    selectedAttributeFilters: [],
    attributeFilters: initialAttributeFilters,
    selectedVendorVariant: getInitialVendorVariant(initialVendorVariants, initialVendorVariantId),
    vendorVariants: orderVendorVariants(initialVendorVariants),
  });

  // lazy query to refetch vendor variants
  const [refetchVendorVariants, {
    loading: busy,
    data
  }] = useLazyQuery<IGetVendorProductGroupByIdData, IGetVendorProductGroupByIdVars>(
    GET_VENDOR_PRODUCT_GROUP,
    {
      fetchPolicy: 'no-cache'
    }
  );

  // listen for updates to selected attribute filters, then refetch vendor variants
  useEffect(() => {
    refetchVendorVariants({
      variables: {
        id,
        // @ts-ignore
        attributeFilters: state.selectedAttributeFilters.map((afi) => ({
          attributeType: afi.attributeTypeId,
          attributeValue: afi.attributeValue
        }))
      }
    });
  }, [state.selectedAttributeFilters]);

  // listen for response from vendor variants fetch request to update state
  useEffect(() => {
    if (data?.vendorProductGroupById) {
      dispatch({
        type: actions.SET_VENDOR_PRODUCT_GROUP,
        payload: data.vendorProductGroupById
      } as ISetVendorProductGroupAction);
    }
  }, [data?.vendorProductGroupById]);

  const router = useRouter();

  // listen for changes to selected vendor variant to update url accordingly
  useEffect(() => {
    let searchParams = new URLSearchParams();
    if (window.location.search) {
      searchParams = new URLSearchParams(window.location.search);
    }
    searchParams.set('vendorVariantId', state.selectedVendorVariant.id.toString());
    router.push(
      `${window.location.pathname}?${searchParams.toString()}${window.location.hash}`,
      undefined,
      { shallow: true }
    );
  }, [state.selectedVendorVariant]);

  // return the state, and simplified event handler callbacks
  return [
    state,
    busy,
    {
      toggleAttributeFilter: (attributeFilter: IAttributeFilterInput) => dispatch({
        type: actions.TOGGLE_FILTER,
        payload: attributeFilter
      } as IToggleFilterAction),
      selectVendorVariant: (vendorVariantId: number) => dispatch({
        type: actions.SET_SELECTED_VENDOR_VARIANT,
        payload: vendorVariantId
      } as ISetSelectedVendorVariantAction)
    }
  ];
}

/**
 * reducer function used in useReducer
 * @todo - this can be tested, write tests for it
 */
function productGroupStateReducer(
  state: IProductGroupPageState,
  action: IAction
): IProductGroupPageState {
  if (isToggleFilterAction(action)) {
    const { attributeTypeId, attributeValue } = action.payload;
    const existingFilterIdx = state.selectedAttributeFilters.findIndex((afi) => {
      return afi.attributeTypeId === attributeTypeId && afi.attributeValue === attributeValue;
    });

    let selectedAttributeFilters = [...state.selectedAttributeFilters];

    if (existingFilterIdx === -1) {
      selectedAttributeFilters = selectedAttributeFilters
        .filter((saf) => saf.attributeTypeId !== attributeTypeId);

      if (isNoResultsFilter(action.payload, state)) {
        selectedAttributeFilters.splice(0, selectedAttributeFilters.length);
      }

      selectedAttributeFilters.push(action.payload);
    } else {
      selectedAttributeFilters.splice(existingFilterIdx, 1);
    }

    return { ...state, selectedAttributeFilters };
  }

  if (isSetVendorProductGroupAction(action)) {
    const { vendorVariants: unorderedVendorVariants, attributeFilters } = action.payload;
    const vendorVariants = orderVendorVariants(unorderedVendorVariants);

    let { selectedVendorVariant } = state;

    // if the currently selected vendor variant doesn't exist in the newly filtered set
    // select a new one.
    if (!vendorVariants.find((vv) => vv.id === state.selectedVendorVariant.id)) {
      const autoSelectedVendorVariant = autoSelectVendorVariant(vendorVariants);
      if (autoSelectedVendorVariant) {
        selectedVendorVariant = autoSelectedVendorVariant;
      }
    }

    return {
      ...state,
      attributeFilters,
      vendorVariants,
      selectedVendorVariant
    };
  }

  if (isSetSelectedVendorVariantAction(action)) {
    const selectedVendorVariant = state.vendorVariants.find((vv) => vv.id === action.payload);

    if (selectedVendorVariant) {
      return { ...state, selectedVendorVariant };
    }

    return state;
  }

  return state;
}

function getInitialVendorVariant(
  vendorVariants: IVendorVariantInternal[],
  vendorVariantId?: string
) {
  if (vendorVariantId) {
    const foundVendorVariant = vendorVariants
      .find((vv: IVendorVariantInternal) => vv.id.toString() === vendorVariantId);

    if (foundVendorVariant) {
      return foundVendorVariant;
    }
  }

  return autoSelectVendorVariant(vendorVariants);
}

/**
 * selects the cheapest in stock vendor variant
 */
export function autoSelectVendorVariant(
  vendorVariants: IVendorVariantInternal[],
): IVendorVariantInternal {
  const orderedVendorVariants = orderVendorVariants(vendorVariants);

  const inStockVendorVariants = orderedVendorVariants
    .filter((vv) => vv.availability === 'in_stock');

  if (inStockVendorVariants.length !== 0) {
    return inStockVendorVariants[0];
  }

  return orderedVendorVariants[0];
}

function orderVendorVariants(
  vendorVariants: IVendorVariantInternal[]
): IVendorVariantInternal[] {
  return orderBy(
    vendorVariants,
    ['salePrice.price', 'retailPrice.price'],
    ['asc', 'asc']
  );
}

function isNoResultsFilter(
  filterInput: IAttributeFilterInput,
  state: IProductGroupPageState
) {
  const { attributeFilters } = state;

  const attributeFilter = attributeFilters.find(
    (af) => af.attributeTypeId === filterInput.attributeTypeId
  );

  if (!attributeFilter) {
    return true;
  }

  const value = attributeFilter.options.find((o) => o.value === filterInput.attributeValue);

  if (!value) {
    return true;
  }

  return value.noResults;
}

// bunch of dispatch action types and type guards below
// implementation details
interface IAction {
  type: actions;
}

interface IToggleFilterAction extends IAction {
  payload: IAttributeFilterInput;
}

interface ISetVendorProductGroupAction extends IAction {
  payload: IVendorProductGroup;
}

interface ISetSelectedVendorVariantAction extends IAction {
  payload: number;
}

function isToggleFilterAction(action: IAction): action is IToggleFilterAction {
  return action.type === actions.TOGGLE_FILTER;
}

function isSetVendorProductGroupAction(action: IAction): action is ISetVendorProductGroupAction {
  return action.type === actions.SET_VENDOR_PRODUCT_GROUP;
}

function isSetSelectedVendorVariantAction(
  action: IAction
): action is ISetSelectedVendorVariantAction {
  return action.type === actions.SET_SELECTED_VENDOR_VARIANT;
}
